/**
 * ProvisionTimingTimeline — the Gantt/waterfall view of a provision, positioning
 * each step by its real wall-clock start so parallelism is visible (container
 * builds overlapping inside "Start app"). Wraps the generic `WaterfallChart`
 * and seeds the "Start app" row expanded so its builds show on first render.
 *
 * @module features/sandboxes/components/overview/ProvisionTimingTimeline
 */
import React, { useCallback, useEffect, useState } from 'react';
import { WaterfallChart } from '@chatsdet/ui';
import type { ProvisionWaterfall } from './provisionTimingWaterfall';

export interface ProvisionTimingTimelineProps {
  waterfall: ProvisionWaterfall;
}

export function ProvisionTimingTimeline({ waterfall }: ProvisionTimingTimelineProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    () => new Set(waterfall.defaultExpandedIds)
  );

  // Re-seed the default-expanded rows when a different run's data arrives (its
  // ids change, so the key changes). Within a run the key is stable, so a user's
  // expand/collapse toggles survive background refetches.
  const defaultKey = waterfall.defaultExpandedIds.join(',');
  useEffect(() => {
    setExpandedIds(new Set(defaultKey ? defaultKey.split(',') : []));
  }, [defaultKey]);

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  return (
    <div className="px-1 py-2">
      <WaterfallChart
        entries={waterfall.entries}
        totalDurationMs={waterfall.totalDurationMs}
        groups={waterfall.groups}
        expandedIds={expandedIds}
        onToggleExpand={toggleExpand}
        compact
      />
    </div>
  );
}

ProvisionTimingTimeline.displayName = 'ProvisionTimingTimeline';
